/**
 * <s-cart-addon> wraps a one-tap "Add" button (data-addon-variant-id) for a cart extra.
 * Deliberately not a /cart/add form: product-option apps hook add-to-cart form submits page-wide
 * and would add the first extra's variant instead. Posts to the Ajax Cart API directly so the
 * drawer never redirects, then refreshes every mounted <s-cart> so the row disappears.
 */
import { announceCartFeedback, getCartErrorMessage, getCartSuccessMessage } from '@theme/cart-feedback';

/**
 * Hidden line item properties that tag a cart extra on the order: which offer, the price the shopper saw,
 * the cart version (Theme settings > Cart > Cart offer version) and where it was shown. Keys start with "_"
 * so the cart and checkout hide them; they stay on the order in the admin for reporting.
 */
function offerProperties(el, price) {
  const props = {
    _cart_offer: el.dataset.offer || '',
    _cart_offer_price: (price || '').replace(/^\+/, '').trim(),
    _cart_offer_version: el.dataset.offerVersion || '',
    _cart_offer_placement: el.dataset.offerPlacement || '',
  };
  return Object.fromEntries(Object.entries(props).filter(([, value]) => value));
}

/**
 * Funnel events for analytics pixels (e.g. Google Analytics through a custom pixel subscribed to these names):
 * cart_offer_viewed, cart_offer_added, cart_offer_add_failed, cart_offer_removed. Each carries the offer handle,
 * cart version, placement and the price the shopper saw. Purchases come from the standard checkout events.
 */
function offerEventData(el, price) {
  const amount = Number.parseFloat(String(price || '').replace(/[^0-9.,]/g, '').replace(/,(?=\d{3}\b)/g, '').replace(',', '.'));
  return {
    offer: el.dataset.offer || '',
    offer_version: el.dataset.offerVersion || '',
    placement: el.dataset.offerPlacement || '',
    price: Number.isFinite(amount) ? amount : null,
    currency: window.Shopify?.currency?.active || '',
  };
}

function publishOfferEvent(name, el, price) {
  if (!el?.dataset.offer) return;
  try { window.Shopify?.analytics?.publish?.(name, offerEventData(el, price)); } catch {}
}

const viewedThisPage = new Set();
function publishOfferView(el) {
  const key = [el.dataset.offer, el.dataset.offerVersion, el.dataset.offerPlacement].join('|');
  if (viewedThisPage.has(key)) return;
  viewedThisPage.add(key);
  const price = el.querySelector('[data-addon-price]')?.textContent
    || el.closest('li')?.querySelector('[data-addon-price]')?.textContent
    || el.dataset.offerPrice;
  publishOfferEvent('cart_offer_viewed', el, price);
}

/**
 * Offer views. Each offer (add-on row, upsell, shipping protection) records itself once it is actually on screen
 * (drawer open, or scrolled into view on the cart page). The cart keeps two hidden attributes that carry over to the
 * order: _cart_offers_shown (comma-separated offer handles seen in this cart) and _cart_offer_version. Orders that saw
 * an offer but didn't take it are then countable, which gives the take rate.
 */
const pendingShown = new Set();
const savedShown = new Set();
let shownVersion = '';
let savedVersion = '';
let shownTimer = 0;
let shownChain = Promise.resolve();

function markOfferShown(el) {
  const offer = el.dataset.offer;
  if (!offer) return;
  const version = el.dataset.offerVersion || '';
  if (savedShown.has(offer) && version === savedVersion) return;
  pendingShown.add(offer);
  if (version) shownVersion = version;
  clearTimeout(shownTimer);
  shownTimer = setTimeout(() => {
    const offers = [...pendingShown];
    pendingShown.clear();
    shownChain = shownChain.then(() => saveShownOffers(offers, shownVersion)).catch(() => {});
  }, 400);
}

async function saveShownOffers(offers, version) {
  const root = window.Shopify?.routes?.root || '/';
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const cart = await (await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })).json();
  if (!cart?.item_count) return;
  const current = cart.attributes || {};
  const existing = String(current._cart_offers_shown || '').split(',').map((s) => s.trim()).filter(Boolean);
  const nextShown = [...new Set([...existing, ...offers])].join(',');
  const attributes = {};
  if (nextShown !== (current._cart_offers_shown || '')) attributes._cart_offers_shown = nextShown;
  if (version && current._cart_offer_version !== version) attributes._cart_offer_version = version;
  if (Object.keys(attributes).length) {
    const response = await fetch(`${root}cart/update.js`, { method: 'POST', headers, body: JSON.stringify({ attributes }) });
    if (!response.ok) return;
  }
  offers.forEach((offer) => savedShown.add(offer));
  if (version) savedVersion = version;
}

/** Calls markOfferShown the first time at least half of the element is visible. */
function watchOfferView(el, signal) {
  if (!el.dataset.offer || !('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    publishOfferView(el);
    markOfferShown(el);
  }, { threshold: 0.5 });
  observer.observe(el);
  signal.addEventListener('abort', () => observer.disconnect(), { once: true });
}

class CartAddon extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#ac.signal });
    // Mug offer photo: open its photos in the cart image viewer, starting on the colour shown.
    this.addEventListener('click', (event) => {
      const thumb = event.target instanceof Element ? event.target.closest('[data-mug-thumb]') : null;
      if (!thumb || !window.cpOpenImageViewer || event.metaKey || event.ctrlKey || event.shiftKey) return;
      let urls = [];
      try { urls = JSON.parse(this.querySelector('script[data-mug-gallery]')?.textContent || '[]'); } catch {}
      if (!urls.length) return;
      event.preventDefault();
      const path = (url) => { try { return new URL(url, location.href).pathname; } catch { return url; } };
      const start = Math.max(0, urls.findIndex((url) => path(url) === path(thumb.href)));
      // Colour swatches in the viewer (snippets/mug-color-sets.liquid): picking one sets the offer's colour.
      let colors = [];
      try { colors = JSON.parse(this.querySelector('script[data-bfcm-mug-colors]')?.textContent || '[]'); } catch {}
      const options = colors.length > 1 ? {
        colors,
        selected: this.querySelector('button[data-addon-variant-id]')?.dataset.addonVariantId,
        onPick: (id) => {
          const name = colors.find((c) => String(c.id) === String(id))?.name;
          const radio = [...this.querySelectorAll('input[type="radio"][data-addon-option]')].find((r) => r.value === name);
          if (!(radio instanceof HTMLInputElement) || radio.checked) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        },
      } : null;
      window.cpOpenImageViewer(urls, start, thumb.dataset.galleryTitle, thumb.dataset.galleryWas || '', options);
    }, { signal: this.#ac.signal });
    watchOfferView(this, this.#ac.signal);
    // Digital copy row with design chips: tick designs (or All); the price and button follow the count.
    if (this.querySelector('[data-digital-pick]')) {
      this.addEventListener('click', (event) => {
        const chip = event.target instanceof Element ? event.target.closest('[data-digital-pick], [data-digital-all]') : null;
        if (!chip || !this.contains(chip)) return;
        event.preventDefault();
        const picks = [...this.querySelectorAll('[data-digital-pick]')];
        if (chip.hasAttribute('data-digital-all')) {
          const allOn = chip.getAttribute('aria-pressed') !== 'true';
          picks.forEach((pick) => pick.setAttribute('aria-pressed', String(allOn)));
        } else {
          chip.setAttribute('aria-pressed', String(chip.getAttribute('aria-pressed') !== 'true'));
        }
        this.#syncDigital();
      }, { signal: this.#ac.signal });
      this.#syncDigital();
    }
    // Upsell rows with option pickers (e.g. Color and Size): point the Add button at the chosen variant.
    if (this.querySelector('[data-addon-option]')) {
      this.addEventListener('change', () => this.#syncVariant(), { signal: this.#ac.signal });
      this.#syncVariant();
    }
  }

  #syncVariant() {
    const button = this.querySelector('button[data-addon-variant-id]');
    const label = this.querySelector('[data-addon-label]');
    const price = this.querySelector('[data-addon-price]');
    let variants = [];
    try { variants = JSON.parse(this.querySelector('script[data-addon-variants]')?.textContent || '[]'); } catch {}
    // Selects (e.g. Size) and swatch radios (e.g. mug Color) both carry their option position.
    const chosen = [];
    for (const input of this.querySelectorAll('[data-addon-option]')) {
      if (input.type === 'radio' && !input.checked) continue;
      chosen[Number(input.dataset.addonOption)] = input.value;
    }
    const match = variants.find((variant) => chosen.every((value, i) => variant.options[i] === value));
    const usable = Boolean(match && match.available);
    if (!button) return;
    if (match) {
      button.dataset.addonVariantId = String(match.id);
      if (price && match.price) price.textContent = match.price;
      // Mug offer: the photo follows the chosen colour (and the gallery opens on it).
      const thumb = this.querySelector('[data-mug-thumb]');
      if (thumb && match.image) {
        const img = thumb.querySelector('img');
        if (img) { img.src = match.image; img.removeAttribute('srcset'); }
        if (match.imageLarge) thumb.href = match.imageLarge;
      }
    }
    button.toggleAttribute('disabled', !usable || button.hasAttribute('data-at-max'));
    if (label) label.textContent = usable ? label.dataset.labelAdd : label.dataset.labelUnavailable;
  }

  #syncDigital() {
    const picks = [...this.querySelectorAll('[data-digital-pick]')];
    const count = picks.filter((pick) => pick.getAttribute('aria-pressed') === 'true').length;
    const all = this.querySelector('[data-digital-all]');
    if (all) all.setAttribute('aria-pressed', String(count === picks.length));
    const label = this.querySelector('[data-digital-add-label]');
    if (label) label.textContent = count > 1 ? (label.dataset.labelMany || '').replace('[n]', count) : label.dataset.labelOne;
    this.querySelector('button[data-addon-variant-id]')?.toggleAttribute('disabled', count === 0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #onClick(event) {
    const button = event.target.closest?.('button[data-addon-variant-id]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();

    if (button.getAttribute('aria-busy') === 'true') return;

    const id = Number.parseInt(button.dataset.addonVariantId || '', 10);
    if (!id) return;

    button?.setAttribute('aria-busy', 'true');
    button?.setAttribute('disabled', '');
    window.setCartBusy?.(true);

    const root = window.Shopify?.routes?.root || '/';
    // The price shown on this row (the upsell's updates with the chosen variant).
    const priceEl = this.querySelector('[data-addon-price]') || this.closest('li')?.querySelector('[data-addon-price]');
    const properties = offerProperties(this, priceEl?.textContent);
    // Matching mug: each one is tied to a shirt number (changeable on the mug's cart line).
    if (button.dataset.nextShirt) {
      const shirt = button.dataset.nextShirt;
      properties['Matching tee design'] = (button.dataset.shirtTemplate || 'Shirt [n]').replace('[n]', shirt);
      properties._matching_tee_design = shirt;
      properties._mug_order = mugOrderStamp();
    }

    // Digital copies: one line per ticked design, tagged with which design it's for (shown on the line and the order).
    let items = [{ id, quantity: 1, properties }];
    const picks = [...this.querySelectorAll('[data-digital-pick][aria-pressed="true"]')];
    if (picks.length) {
      const prices = (() => { try { return JSON.parse(this.dataset.digitalPrices || '[]'); } catch { return []; } })();
      items = picks.map((pick) => ({
        id,
        quantity: 1,
        properties: {
          ...properties,
          _cart_offer_price: String(prices[0] || '').replace(/^\+/, '').trim(),
          [button.dataset.digitalLabel || 'For design']: pick.dataset.digitalFor || '',
          _digital_for: pick.dataset.digitalKey || '',
        },
      }));
    }

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        publishOfferEvent('cart_offer_add_failed', this, priceEl?.textContent);
        throw new Error(result?.description || result?.message || '');
      }
      publishOfferEvent('cart_offer_added', this, priceEl?.textContent);

      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.({ animate: false });
      await announceCartFeedback(getCartSuccessMessage([{ quantity: 1 }]));
    } catch (error) {
      await announceCartFeedback(error?.message || getCartErrorMessage(error), { toast: true });
    } finally {
      window.setCartBusy?.(false);
      if (button?.isConnected) {
        button.removeAttribute('aria-busy');
        button.removeAttribute('disabled');
      }
    }
  }
}

if (!customElements.get('s-cart-addon')) customElements.define('s-cart-addon', CartAddon);

/**
 * Matching mug controls: the cart offer's "−" removes the newest mug line, and the shirt chips on the first mug line
 * select (snippets/cart-line-item.liquid) re-points that mug at another shirt by rewriting its properties.
 */
// When a mug was added (hidden _mug_order, kept through colour and shirt swaps): the mugs card lists them oldest
// first so a new mug lands under the ones already there. Always 13 digits and unique, so it sorts as text.
let lastMugOrder = 0;
function mugOrderStamp() {
  lastMugOrder = Math.max(Date.now(), lastMugOrder + 1);
  return String(lastMugOrder);
}
async function changeMugLine(body, busyEl) {
  if (busyEl?.getAttribute('aria-busy') === 'true') return;
  busyEl?.setAttribute('aria-busy', 'true');
  window.setCartBusy?.(true);
  const root = window.Shopify?.routes?.root || '/';
  try {
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result?.description || result?.message || '');
    }
    await customElements.whenDefined('s-cart');
    await customElements.get('s-cart')?.refreshMountedCarts?.({ animate: false });
  } catch (error) {
    await announceCartFeedback(error?.message || getCartErrorMessage(error), { toast: true });
    // Put the mug rows back to what the cart really holds (the dropdown already shows the failed pick).
    try { await customElements.get('s-cart')?.refreshMountedCarts?.({ animate: false }); } catch {}
  } finally {
    window.setCartBusy?.(false);
    if (busyEl?.isConnected) busyEl.removeAttribute('aria-busy');
  }
}
document.addEventListener('click', (event) => {
  const minus = event.target instanceof Element ? event.target.closest('[data-mug-minus]') : null;
  if (!minus?.dataset.lineKey) return;
  event.preventDefault();
  changeMugLine({ id: minus.dataset.lineKey, quantity: 0 }, minus);
});
// Matching mugs card: re-point a mug at another shirt with one cart change (rewrites its line properties, so the
// order shows the right "Matching tee design"). Shirts other mugs already use are disabled in the dropdown.
document.addEventListener('change', (event) => {
  const select = event.target instanceof Element ? event.target.closest('[data-mug-shirt-select]') : null;
  if (!select?.value) return;
  // Instant feedback: the chosen shirt drops out of "Add a mug for".
  const card = select.closest('[data-mug-umbrella]');
  const chosen = new Set([...(card?.querySelectorAll('[data-mug-shirt-select]') || [])].map((s) => s.value));
  card?.querySelectorAll('[data-mug-chip]').forEach((chip) => { chip.hidden = chosen.has(chip.dataset.shirt); });
  let properties = {};
  try { properties = JSON.parse(select.dataset.lineProperties || '{}') || {}; } catch {}
  // A line without details comes through as [] and the cart only accepts {}.
  if (Array.isArray(properties) || typeof properties !== 'object') properties = {};
  properties['Matching tee design'] = select.selectedOptions[0]?.textContent.trim() || '';
  properties._matching_tee_design = select.value;
  delete properties._matching_tee_line;
  repointMug(select, properties);
});
/**
 * Re-point a mug at another shirt the same way colour swaps work (proven reliable): add the same mug with the new
 * shirt details, then remove the old line. Editing properties in place didn't stick, leaving "Pick a shirt".
 */
async function rewriteMugLine({ key, variantId, quantity, properties }) {
  const root = window.Shopify?.routes?.root || '/';
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const added = await fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1, properties }] }),
  });
  if (!added.ok) {
    const result = await added.json().catch(() => ({}));
    throw new Error(result?.description || result?.message || '');
  }
  await fetch(`${root}cart/change.js`, { method: 'POST', headers, body: JSON.stringify({ id: key, quantity: 0 }) });
}
// Shirt picks still being saved (old line key -> shirt): any refresh in the meantime keeps showing the pick
// instead of flashing back to "Pick a shirt" with every chip.
const pendingMugShirts = new Map();
function applyPendingMugs() {
  if (!pendingMugShirts.size) return;
  document.querySelectorAll('[data-mug-umbrella]').forEach((card) => {
    card.querySelectorAll('select[data-mug-shirt-select]').forEach((select) => {
      const shirt = pendingMugShirts.get(select.dataset.lineKey);
      if (!shirt || !select.querySelector(`option[value="${CSS.escape(shirt)}"]`)) return;
      select.querySelector('option[value=""]')?.remove();
      select.value = shirt;
      select.setAttribute('aria-busy', 'true');
      card.querySelectorAll(`[data-mug-chip][data-shirt="${CSS.escape(shirt)}"]`).forEach((chip) => { chip.hidden = true; });
    });
  });
}
document.addEventListener('cart:reconciled', applyPendingMugs);
async function repointMug(select, properties) {
  if (select.getAttribute('aria-busy') === 'true') return;
  select.setAttribute('aria-busy', 'true');
  window.setCartBusy?.(true);
  pendingMugShirts.set(select.dataset.lineKey, String(properties._matching_tee_design));
  select.querySelector('option[value=""]')?.remove();
  try {
    await rewriteMugLine({
      key: select.dataset.lineKey,
      variantId: select.dataset.variantId,
      quantity: Number(select.dataset.lineQuantity) || 1,
      properties,
    });
  } catch (error) {
    await announceCartFeedback(error?.message || getCartErrorMessage(error), { toast: true });
  } finally {
    // Saved (or failed): the next refresh shows the cart's real state.
    pendingMugShirts.delete(select.dataset.lineKey);
    try {
      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.({ animate: false });
    } catch {}
    window.setCartBusy?.(false);
    if (select.isConnected) select.removeAttribute('aria-busy');
  }
}
// Matching mugs card: a colour swatch swaps that mug to the chosen variant (same shirt and details).
document.addEventListener('change', async (event) => {
  const radio = event.target instanceof Element ? event.target.closest('[data-mug-color]') : null;
  if (!radio?.checked || !radio.dataset.lineKey) return;
  const row = radio.closest('li') || radio;
  if (row.getAttribute('aria-busy') === 'true') return;
  // Show the new colour's photo straight away while the cart updates.
  const rowThumb = row.querySelector('[data-bfcm-mug-thumb] img');
  if (rowThumb && radio.dataset.image) { rowThumb.src = radio.dataset.image; rowThumb.alt = radio.dataset.colorName || ''; }
  const colorName = row.querySelector('[data-mug-color-name]');
  if (colorName && radio.dataset.colorName) colorName.textContent = radio.dataset.colorName;
  row.setAttribute('aria-busy', 'true');
  window.setCartBusy?.(true);
  const root = window.Shopify?.routes?.root || '/';
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  let properties = {};
  try { properties = JSON.parse(radio.dataset.lineProperties || '{}') || {}; } catch {}
  if (Array.isArray(properties) || typeof properties !== 'object') properties = {};
  try {
    const added = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ items: [{ id: Number(radio.value), quantity: Number(radio.dataset.lineQuantity) || 1, properties }] }),
    });
    if (!added.ok) {
      const result = await added.json().catch(() => ({}));
      throw new Error(result?.description || result?.message || '');
    }
    await fetch(`${root}cart/change.js`, { method: 'POST', headers, body: JSON.stringify({ id: radio.dataset.lineKey, quantity: 0 }) });
    await customElements.whenDefined('s-cart');
    await customElements.get('s-cart')?.refreshMountedCarts?.({ animate: false });
  } catch (error) {
    await announceCartFeedback(error?.message || getCartErrorMessage(error), { toast: true });
    await customElements.get('s-cart')?.refreshMountedCarts?.({ animate: false });
  } finally {
    window.setCartBusy?.(false);
    if (row.isConnected) row.removeAttribute('aria-busy');
  }
});
document.addEventListener('click', async (event) => {
  const chip = event.target instanceof Element ? event.target.closest('[data-mug-chip]') : null;
  const row = chip?.closest('[data-mug-chips]');
  if (!row) return;
  event.preventDefault();
  if (chip.dataset.lineKey) {
    changeMugLine({ id: chip.dataset.lineKey, quantity: 0 }, chip);
    return;
  }
  if (chip.getAttribute('aria-busy') === 'true') return;
  chip.setAttribute('aria-busy', 'true');
  window.setCartBusy?.(true);
  const root = window.Shopify?.routes?.root || '/';
  const shirt = chip.dataset.shirt;
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{
        id: Number(row.dataset.variantId),
        quantity: 1,
        properties: {
          'Matching tee design': (row.dataset.shirtTemplate || 'Shirt [n]').replace('[n]', shirt),
          _matching_tee_design: shirt,
          _mug_order: mugOrderStamp(),
          _cart_offer: 'bfcm-mug',
          _cart_offer_placement: 'cart-line',
          _cart_offer_price: row.dataset.offerPrice || '',
        },
      }] }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result?.description || result?.message || '');
    }
    await customElements.whenDefined('s-cart');
    await customElements.get('s-cart')?.refreshMountedCarts?.({ animate: false });
  } catch (error) {
    await announceCartFeedback(error?.message || getCartErrorMessage(error), { toast: true });
  } finally {
    window.setCartBusy?.(false);
    if (chip.isConnected) chip.removeAttribute('aria-busy');
  }
});

/**
 * <s-shipping-protection> — opt-in switch that adds or removes the shipping protection line.
 * Uses the Ajax Cart API directly, then refreshes every mounted <s-cart>.
 */
class ShippingProtection extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', (event) => {
      const toggle = event.target.closest?.('[data-protection-toggle]');
      if (toggle) this.#toggle(toggle);
    }, { signal: this.#ac.signal });
    watchOfferView(this, this.#ac.signal);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /** Writes the pre-rendered toggled total into every cart summary; returns an undo function. */
  #previewTotal() {
    const nextTotal = this.dataset.toggledTotal;
    if (!nextTotal) return () => {};
    const targets = [...document.querySelectorAll('[data-cart-total]')].map((el) => el.querySelector('bdi') || el);
    const previous = targets.map((el) => el.textContent);
    targets.forEach((el) => { el.textContent = nextTotal; });
    return () => targets.forEach((el, i) => { if (el.isConnected) el.textContent = previous[i]; });
  }

  async #toggle(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const turningOn = button.getAttribute('aria-checked') !== 'true';
    const root = window.Shopify?.routes?.root || '/';
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-checked', String(turningOn));
    window.setCartBusy?.(true);
    // Show the new total straight away; the cart refresh below confirms it.
    const restoreTotals = this.#previewTotal();

    let saved = false;
    try {
      const response = turningOn
        ? await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ items: [{ id: Number(this.dataset.variantId), quantity: 1, properties: offerProperties(this, this.dataset.offerPrice) }] }),
        })
        : await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ id: this.dataset.lineKey, quantity: 0 }),
        });
      if (!response.ok) {
        if (turningOn) publishOfferEvent('cart_offer_add_failed', this, this.dataset.offerPrice);
        throw new Error('Shipping protection update failed');
      }
      saved = true;
      publishOfferEvent(turningOn ? 'cart_offer_added' : 'cart_offer_removed', this, this.dataset.offerPrice);

      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.({ animate: false });
    } catch (error) {
      // Only flip the switch back if the cart change itself failed, not a slow refresh.
      if (!saved) {
        if (button.isConnected) button.setAttribute('aria-checked', String(!turningOn));
        restoreTotals();
      }
      await announceCartFeedback(getCartErrorMessage(error), { toast: true });
    } finally {
      window.setCartBusy?.(false);
      if (button.isConnected) button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-shipping-protection')) customElements.define('s-shipping-protection', ShippingProtection);

/**
 * Apps (e.g. Live Product Options' "Edit" button) inject links/buttons into cart rows ([data-cart-item]).
 * Our details live in a collapsed box, so move anything the app adds — and any app "Edit" control anywhere
 * in the row — into the .cart-details-actions slot next to the photo previews, where it's always visible.
 */
const EDIT_TEXT = /\bedit\b/i;

function isOurs(el) {
  return el.closest('[data-theme-own]') !== null || el.hasAttribute('commandfor');
}

function liftAppNodes(root = document) {
  const rows = root.matches?.('[data-cart-item]') ? [root] : [...(root.querySelectorAll?.('[data-cart-item]') || [])];
  for (const row of rows) {
    const slot = row.querySelector('.cart-details-actions');
    const fallbackAnchor = row.querySelector('.cart-details') || row.lastElementChild;

    const candidates = new Set();
    // 1) Anything an app appended inside the collapsed body that isn't ours.
    // Packs show a per-shirt view and keep the plain list invisible (.cart-details-legacy) for the app to attach to.
    for (const body of row.querySelectorAll('.cart-details-body, .cart-details-legacy')) {
      for (const child of body.children) if (!child.hasAttribute('data-theme-own')) candidates.add(child);
      for (const nested of body.querySelectorAll('[data-theme-own] a:not([data-theme-own]), [data-theme-own] button')) {
        if (!nested.closest('.cart-details-actions')) candidates.add(nested);
      }
    }
    // 2) Any "Edit" link/button the app placed elsewhere in the row.
    for (const control of row.querySelectorAll('a, button, [role="button"]')) {
      if (slot?.contains(control) || isOurs(control)) continue;
      if (EDIT_TEXT.test(control.textContent || '') || EDIT_TEXT.test(control.getAttribute('aria-label') || '')) candidates.add(control);
    }

    for (const node of candidates) {
      const formerParent = node.parentElement;
      if (slot) {
        if (!slot.contains(node)) slot.append(node);
      } else if (fallbackAnchor && node !== fallbackAnchor) {
        fallbackAnchor.after(node);
      }
      hideEmptyWrappers(formerParent, row);
    }
  }
}

/** The app wraps its button in its own container; once the button moves, hide that now-empty box (and empty parents). */
function hideEmptyWrappers(el, row) {
  while (el && el !== row && row.contains(el) && !el.hasAttribute('data-theme-own') && !el.closest('.cart-details-actions')) {
    const empty = el.children.length === 0 || [...el.children].every((child) => child.hidden || child.style.display === 'none');
    if (!empty || (el.textContent || '').trim() !== '') return;
    el.style.display = 'none';
    el = el.parentElement;
  }
}

let liftQueued = false;
function queueLift() {
  if (liftQueued) return;
  liftQueued = true;
  requestAnimationFrame(() => {
    liftQueued = false;
    liftAppNodes();
  });
}

liftAppNodes();
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    const target = mutation.target;
    if (target instanceof Element && (target.closest('[data-cart-item]') || target.querySelector?.('[data-cart-item]'))) {
      queueLift();
      return;
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

/**
 * Edit-item clean-up. When a shopper taps an app's Edit button on a cart line, remember that line's key, the
 * product it belongs to and which lines were in the cart. After the edited version is added from that product
 * page, remove the original line, unless the app already did. The original is only removed when a new line
 * has appeared since Edit was tapped, so an in-place edit that keeps the same line is never deleted.
 */
const EDIT_TAP_TEXT = /\bedit\b/i;
window.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const row = target?.closest('[data-cart-item][data-cart-item-key]');
  if (!row) return;
  const control = target.closest('a, button, [role="button"]');
  if (!control || !row.contains(control) || !EDIT_TAP_TEXT.test(control.textContent || control.getAttribute('aria-label') || '')) return;
  if (control.closest('[data-theme-own]') && !control.closest('.cart-details-actions')) return;

  let path = '';
  try { path = new URL(row.dataset.cartItemUrl || '', location.href).pathname; } catch {}
  const container = row.parentElement?.closest('[id], s-cart, dialog') || document;
  const keys = [...container.querySelectorAll('[data-cart-item-key]')].map((el) => el.dataset.cartItemKey);
  try {
    sessionStorage.setItem('cp:editing', JSON.stringify({ key: row.dataset.cartItemKey, path, keys, t: Date.now() }));
  } catch {}
}, { capture: true });

let removingEdited = null;
function removeEditedOriginal() {
  removingEdited ||= doRemoveEditedOriginal().finally(() => { removingEdited = null; });
  return removingEdited;
}

/** Returns true when the original line was removed. */
async function doRemoveEditedOriginal() {
  let editing = null;
  try { editing = JSON.parse(sessionStorage.getItem('cp:editing') || 'null'); } catch {}
  if (!editing || !editing.key) return false;
  if (Date.now() - editing.t > 30 * 60 * 1000) {
    try { sessionStorage.removeItem('cp:editing'); } catch {}
    return false;
  }
  const root = window.Shopify?.routes?.root || '/';
  try {
    const cart = await (await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })).json();
    const original = cart.items.find((item) => item.key === editing.key);
    if (!original) {
      // The app already replaced it: only the mugs picked on the edit page are left to do.
      sessionStorage.removeItem('cp:editing');
      return await applyEditedMugs(editing);
    }
    const before = new Set(editing.keys || []);
    // The edited version: a line of the same product that wasn't in the cart when Edit was tapped.
    const replacement = cart.items.find((item) => !before.has(item.key) && item.key !== original.key && item.product_id === original.product_id);
    if (!replacement) {
      // Saved without changing the shirt: the app keeps the same line, so no new one ever appears. If Add was
      // pressed on the edit page a few seconds ago (the app has had time to finish), apply the mug picks to it.
      const submitted = Number(editing.mug?.submitted) || 0;
      if (submitted && (window.cpReturningToDrawer || Date.now() - submitted > 8000)) {
        sessionStorage.removeItem('cp:editing');
        return await applyEditedMugs(editing, true);
      }
      return false;
    }
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: editing.key, quantity: 0 }),
    });
    if (!response.ok) return false;
    sessionStorage.removeItem('cp:editing');
    await applyEditedMugs(editing);
    return true;
  } catch {
    // Network hiccup: leave the cart as it is rather than guess.
    return false;
  }
}


/**
 * Mugs picked on the edit page are saved onto the edit (cp:editing.mug), not the general mug pick, and applied right
 * here once the edited version is in the cart: drop the edited line's old mugs, add one per ticked shirt.
 * Returns true when the cart changed.
 */
async function applyEditedMugs(editing, inPlace = false) {
  const mug = editing?.mug;
  if (!mug?.bundleProductId) return false;
  const root = window.Shopify?.routes?.root || '/';
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
  try {
    const cart = await (await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })).json();
    const before = new Set(editing.keys || []);
    const bundles = cart.items.filter((item) => item.product_id === mug.bundleProductId);
    // In place: the shopper saved without changing the shirt, so the app kept the same line.
    const replacement = inPlace
      ? bundles.find((item) => item.key === editing.key)
      : bundles.find((item) => !before.has(item.key) && item.key !== editing.key);
    if (!replacement) return false;
    const shirtsOf = (item) => (parseInt(String(item.variant_title || '').charAt(0), 10) || 1) * item.quantity;
    // Mug shirt numbers run through the bundles oldest first; the cart lists newest first.
    let offset = 0;
    for (const line of [...bundles].reverse()) {
      if (line.key === replacement.key) break;
      offset += shirtsOf(line);
    }
    const count = shirtsOf(replacement);
    let changed = false;
    const updates = {};
    for (const key of mug.oldMugKeys || []) {
      if (cart.items.some((item) => item.key === key)) updates[key] = 0;
    }
    if (Object.keys(updates).length) {
      const response = await fetch(`${root}cart/update.js`, { method: 'POST', headers: json, body: JSON.stringify({ updates }) });
      changed ||= response.ok;
    }
    const items = (mug.shirts || []).filter((shirt) => shirt >= 1 && shirt <= count).map((shirt) => ({
      id: Number(mug.variants?.[shirt]) || mug.variantId,
      quantity: 1,
      properties: {
        'Matching tee design': (mug.shirtTemplate || 'Shirt [n]').replace('[n]', String(offset + shirt)),
        _matching_tee_design: String(offset + shirt),
        _mug_order: mugOrderStamp(),
        _cart_offer: 'bfcm-mug',
        _cart_offer_placement: 'product',
        _cart_offer_price: mug.price || '',
      },
    }));
    if (items.length) {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: json, body: JSON.stringify({ items }) });
      changed ||= response.ok;
    }
    return changed;
  } catch {
    return false;
  }
}


// Edit page: remember that Add was pressed while mugs are picked for this edit, so applyEditedMugs can run even when
// the app saves the shirt unchanged (same cart line). Checks again a few seconds later in case the app stays put.
function markEditSubmitted(event) {
  if (!location.pathname.includes('/products/')) return;
  const target = event.target instanceof Element ? event.target : null;
  if (event.type === 'click') {
    const control = target?.closest('button, [role="button"], input[type="submit"]');
    if (!control || !control.closest('s-product-form, .pdp-form')) return;
    const looksLikeAdd = control.getAttribute('name') === 'add' || control.getAttribute('type') === 'submit'
      || /add to cart|update|save/i.test(control.textContent || control.value || '');
    if (!looksLikeAdd) return;
  } else if (!target?.closest('s-product-form, .pdp-form')) {
    return;
  }
  let editing = null;
  try { editing = JSON.parse(sessionStorage.getItem('cp:editing') || 'null'); } catch {}
  if (!editing?.mug) return;
  editing.mug.submitted = Date.now();
  try { sessionStorage.setItem('cp:editing', JSON.stringify(editing)); } catch { return; }
  for (const wait of [3500, 9000]) {
    setTimeout(async () => {
      if (await removeEditedOriginal()) {
        try { await customElements.get('s-cart')?.refreshMountedCarts?.(); } catch {}
      }
    }, wait);
  }
}
window.addEventListener('click', markEditSubmitted, { capture: true });
window.addEventListener('submit', markEditSubmitted, { capture: true });

// Backup for when the app opens the drawer itself: whenever the cart drawer opens, finish any pending edit.
function watchDrawerForEdits() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  new MutationObserver(async () => {
    if (!drawer.hasAttribute('open')) return;
    if (await removeEditedOriginal()) {
      try { await customElements.get('s-cart')?.refreshMountedCarts?.(); } catch {}
    }
  }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchDrawerForEdits, { once: true });
} else {
  watchDrawerForEdits();
}
// Also on page load (e.g. the app sent the shopper to another page after saving).
window.addEventListener('load', async () => {
  if (await removeEditedOriginal()) {
    try { await customElements.get('s-cart')?.refreshMountedCarts?.(); } catch {}
  }
});

// Shared with s-product-form.js, which finishes the edit right after the edited version is added.
window.cpRemoveEditedOriginal = removeEditedOriginal;

// Black Friday PromptTee: the product-options app can add the tee without the theme's hidden "Digital copy"
// property. Rows rendered without it carry data-bfcm-tag-name/value (snippets/cart-line-item.liquid); add it to
// the line, keeping every existing property, so the free copy shows at checkout and on the order. Once per line.
let taggingBfcm = null;
function tagBfcmDigital() {
  taggingBfcm ||= doTagBfcmDigital().finally(() => { taggingBfcm = null; });
  return taggingBfcm;
}

async function doTagBfcmDigital() {
  const rows = [...document.querySelectorAll('[data-cart-item-key][data-bfcm-tag-name]')];
  if (!rows.length) return false;
  let tried = {};
  try { tried = JSON.parse(sessionStorage.getItem('cp:bfcm-tagged') || '{}'); } catch {}
  const root = window.Shopify?.routes?.root || '/';
  let changed = false;
  try {
    const cart = await (await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })).json();
    const seen = new Set();
    for (const row of rows) {
      const key = row.dataset.cartItemKey;
      const name = row.dataset.bfcmTagName;
      if (!key || !name || seen.has(key) || tried[key]) continue;
      seen.add(key);
      const item = cart.items.find((line) => line.key === key);
      if (!item || item.properties?.[name]) continue;
      tried[key] = true;
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: item.quantity, properties: { ...(item.properties || {}), [name]: row.dataset.bfcmTagValue } }),
      });
      if (response.ok) changed = true;
    }
  } catch {
    // Network hiccup: try again on the next drawer open or page load.
    return false;
  }
  try { sessionStorage.setItem('cp:bfcm-tagged', JSON.stringify(tried)); } catch {}
  return changed;
}

// Black Friday free extras (Theme settings > Cart > Free extras, rendered as #cp-bfcm-free in snippets/cart-drawer.liquid):
// exactly one of each while the Black Friday product is in the cart, none otherwise, so they can't be bought alone.
function bfcmFreeConfig() {
  try { return JSON.parse(document.getElementById('cp-bfcm-free')?.textContent || 'null'); } catch { return null; }
}

// Matching mug tick (templates/product.bfcm.liquid): remembered in sessionStorage with the bundle lines already in the
// cart, so only the tee added after ticking brings the mug along.
const MUG_WISH_KEY = 'cp:bfcm-mug';
function readMugWish() {
  try {
    const wish = JSON.parse(sessionStorage.getItem(MUG_WISH_KEY) || 'null');
    if (!wish || !wish.variantId || Date.now() - wish.t > 30 * 60 * 1000) return null;
    wish.keys ||= [];
    // An edit can untick every mug (count 0: just drop the old ones).
    wish.count = wish.replaces && Array.isArray(wish.shirts) ? wish.shirts.length : Math.max(1, Number(wish.count) || 1);
    wish.replaceMugKeys = Array.isArray(wish.replaceMugKeys) ? wish.replaceMugKeys : [];
    return wish;
  } catch { return null; }
}
function clearMugWish() {
  try { sessionStorage.removeItem(MUG_WISH_KEY); } catch {}
}
// Bundle page mug card: tick the shirts that get a matching mug (a single tick on 1x packs), each with its own colour.
function syncMugRows(card) {
  card.querySelectorAll('[data-bfcm-mug-toggle]').forEach((input) => {
    const row = card.querySelector(`[data-bfcm-mug-row="${input.dataset.bfcmMugShirt || 1}"]`);
    if (row) row.hidden = !input.checked;
  });
}
// Browsers can restore ticks on back/forward, so line the colour rows up with them on load.
document.querySelectorAll('[data-bfcm-mug]').forEach(syncMugRows);
// A fresh visit starts with an unticked mug card, so a mug pick still stored is from an earlier visit: drop it, or
// it would ride along with the next shirt. Exception: back from the app's /cart hand-off (edit or add), the pick
// is still waiting for the cart sync to add it. Ticks the browser restored (back/forward) are re-saved.
if (document.querySelector('[data-bfcm-mug]') && !window.cpReturningToDrawer) {
  clearMugWish();
  document.addEventListener('DOMContentLoaded', () => {
    const card = document.querySelector('[data-bfcm-mug]');
    if (card?.querySelector('[data-bfcm-mug-toggle]:checked')) saveMugWish(card);
  }, { once: true });
}
// Bundle page mug rows: the row thumbnail (and the card's main image) follow the colour picked for that shirt.
function showMugColor(card, radio) {
  const row = radio.closest('[data-bfcm-mug-row]');
  const thumb = row?.querySelector('[data-bfcm-mug-thumb] img');
  if (thumb && radio.dataset.image) {
    thumb.src = radio.dataset.image;
    thumb.alt = radio.dataset.colorName || '';
  }
  const main = card.querySelector('[data-bfcm-mug-main]');
  if (main && radio.dataset.image) main.src = radio.dataset.image;
  const name = row?.querySelector('[data-bfcm-mug-color-name]');
  if (name && radio.dataset.colorName) name.textContent = radio.dataset.colorName;
}
// Tap the mug (card or a row thumbnail): open its photos large. With colour sets (snippets/mug-color-sets.liquid)
// the viewer shows colour swatches and that colour's angles; picking a colour there becomes the mug colour choice.
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-bfcm-mug-thumb]') : null;
  if (!button || typeof window.cpOpenImageViewer !== 'function') return;
  const card = button.closest('[data-bfcm-mug], [data-mug-umbrella]');
  const row = button.closest('[data-bfcm-mug-row]');
  const chosenRadio = (row || button.closest('li'))?.querySelector('[data-bfcm-mug-color]:checked, [data-mug-color]:checked')
    || card?.querySelector('[data-bfcm-mug-row] [data-bfcm-mug-color]:checked');
  const chosen = chosenRadio?.dataset.imageLarge || '';
  let gallery = [];
  try { gallery = JSON.parse(card?.querySelector('[data-bfcm-mug-gallery]')?.textContent || '[]'); } catch {}
  let colors = [];
  try { colors = JSON.parse(card?.querySelector('[data-bfcm-mug-colors]')?.textContent || '[]'); } catch {}
  const urls = chosen ? [chosen, ...gallery.filter((url) => url !== chosen)] : gallery;
  if (!urls.length) return;
  event.preventDefault();
  let options = null;
  if (card?.matches('[data-mug-umbrella]') && colors.length > 1 && button.hasAttribute('data-mug-browse')) {
    // Cart mugs card's big photo: browse colours only. Several mugs can be in the cart, so this changes none of them.
    options = { colors, selected: card.querySelector('[data-mug-color]:checked')?.value };
  } else if (card?.matches('[data-mug-umbrella]') && colors.length > 1) {
    // Cart mug line: the colour picked in the viewer swaps that mug when the viewer closes (one cart update,
    // so the cart doesn't refresh behind the viewer on every tap).
    const li = button.closest('li');
    let pending = null;
    options = {
      colors,
      selected: chosenRadio?.value,
      onPick(id) { pending = id; },
    };
    document.getElementById('cart-image-viewer')?.addEventListener('close', () => {
      if (!pending || !li?.isConnected) return;
      const radio = li.querySelector(`[data-mug-color][value="${pending}"]`);
      if (!(radio instanceof HTMLInputElement) || radio.checked || radio.disabled) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }, { once: true });
  } else if (card?.matches('[data-bfcm-mug]') && colors.length > 1) {
    const rows = [...card.querySelectorAll('[data-bfcm-mug-row]')];
    options = {
      colors,
      selected: chosenRadio?.value || card.dataset.bfcmMugVariant,
      onPick(id) {
        // A row's own photos change that shirt's mug. The card's photos change every shirt not ticked yet
        // (and the only mug on a 1-shirt pack), so the colour is ready when they tick the box.
        const targets = row ? [row] : rows.filter((r) => r.hidden || rows.length === 1);
        targets.forEach((r) => {
          const radio = r.querySelector(`[data-bfcm-mug-color][value="${id}"]`);
          if (!(radio instanceof HTMLInputElement) || radio.checked || radio.disabled) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
      },
    };
  }
  window.cpOpenImageViewer(urls, 0, button.dataset.galleryTitle || button.getAttribute('aria-label') || '', button.dataset.galleryWas || '', options);
});
let mugWishSavedHere = false;
async function saveMugWish(card) {
  mugWishSavedHere = true;
  const pickedShirts = () => [...card.querySelectorAll('[data-bfcm-mug-toggle]:checked')]
    .map((input) => Number(input.dataset.bfcmMugShirt) || 1);
  // Editing a bundle from the cart: even with nothing ticked, remember the edit so its old mugs are dropped.
  if (!pickedShirts().length && !mugEditContext) { clearMugWish(); return; }
  let keys = readMugWish()?.keys || null;
  if (!keys) {
    keys = [];
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await (await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })).json();
      keys = cart.items.filter((item) => item.product_id === Number(card.dataset.bfcmBundleProduct)).map((item) => item.key);
    } catch {}
  }
  const shirts = pickedShirts();
  if (!shirts.length && !mugEditContext) { clearMugWish(); return; }
  // Colour per ticked shirt (falls back to the card's default colour).
  const variants = {};
  for (const shirt of shirts) {
    const picked = card.querySelector(`[data-bfcm-mug-row="${shirt}"] [data-bfcm-mug-color]:checked`);
    variants[shirt] = Number(picked?.value) || Number(card.dataset.bfcmMugVariant);
  }
  // Edit page: save the picks onto the edit itself; they're applied when the edited version replaces the old line
  // (applyEditedMugs), the same step that removes the old shirt, so they survive the app's /cart hand-off.
  if (mugEditContext) {
    try {
      const editing = JSON.parse(sessionStorage.getItem('cp:editing') || 'null');
      if (editing?.key === mugEditContext.replaces) {
        editing.mug = {
          bundleProductId: Number(card.dataset.bfcmBundleProduct),
          variantId: Number(card.dataset.bfcmMugVariant),
          shirts,
          variants,
          oldMugKeys: mugEditContext.mugKeys || [],
          price: card.dataset.bfcmMugPrice || '',
          shirtTemplate: card.dataset.bfcmMugTemplate || '',
        };
        sessionStorage.setItem('cp:editing', JSON.stringify(editing));
        clearMugWish();
        return;
      }
    } catch {}
  }
  try {
    sessionStorage.setItem(MUG_WISH_KEY, JSON.stringify({
      variantId: Number(card.dataset.bfcmMugVariant),
      productId: Number(card.dataset.bfcmMugProduct),
      shirts,
      variants,
      count: shirts.length,
      keys,
      price: card.dataset.bfcmMugPrice || '',
      shirtTemplate: card.dataset.bfcmMugTemplate || '',
      // Edit: the cart line being replaced and its mugs (swapped for these picks once the edited line arrives).
      replaces: mugEditContext?.replaces || '',
      replaceMugKeys: mugEditContext?.mugKeys || [],
      t: Date.now(),
    }));
  } catch {}
}

// Editing a bundle from the cart (Edit stores cp:editing): tick the mugs that line already has, in their colours,
// so adding the edited version swaps those mugs instead of adding more on top.
let mugEditContext = null;
async function prefillMugsForEdit() {
  const card = document.querySelector('[data-bfcm-mug]');
  if (!card) return;
  let editing = null;
  try { editing = JSON.parse(sessionStorage.getItem('cp:editing') || 'null'); } catch {}
  if (!editing?.key || Date.now() - editing.t > 30 * 60 * 1000) return;
  let cart = null;
  try {
    const root = window.Shopify?.routes?.root || '/';
    cart = await (await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })).json();
  } catch { return; }
  const bundleId = Number(card.dataset.bfcmBundleProduct);
  const mugId = Number(card.dataset.bfcmMugProduct);
  const original = cart?.items?.find((item) => item.key === editing.key);
  if (!original || original.product_id !== bundleId) return;
  // Edit already saved (the app sends shoppers via /cart back here): the edited version is in the cart, so leave
  // the mug picks alone for the cart sync to add, rather than re-saving them from this fresh, unticked card.
  const before = new Set(editing.keys || []);
  if (cart.items.some((item) => item.product_id === bundleId && item.key !== original.key && !before.has(item.key))) return;
  const shirtsOf = (item) => (parseInt(String(item.variant_title || '').charAt(0), 10) || 1) * item.quantity;
  // Mug shirt numbers run through the bundles oldest first; the cart lists newest first.
  let offset = 0;
  for (const line of cart.items.filter((item) => item.product_id === bundleId).reverse()) {
    if (line.key === original.key) break;
    offset += shirtsOf(line);
  }
  const count = shirtsOf(original);
  const mugs = cart.items
    .filter((item) => item.product_id === mugId)
    .map((item) => ({ item, shirt: Number(item.properties?._matching_tee_design) || 0 }))
    .filter(({ shirt }) => shirt > offset && shirt <= offset + count);
  mugEditContext = { replaces: original.key, mugKeys: mugs.map(({ item }) => item.key) };
  if (!mugs.length) return;
  for (const { item, shirt } of mugs) {
    const local = shirt - offset;
    const toggle = card.querySelector(`[data-bfcm-mug-toggle][data-bfcm-mug-shirt="${local}"]`);
    if (!(toggle instanceof HTMLInputElement)) continue;
    toggle.checked = true;
    const radio = card.querySelector(`[data-bfcm-mug-row="${local}"] [data-bfcm-mug-color][value="${item.variant_id}"]`);
    if (radio instanceof HTMLInputElement) { radio.checked = true; showMugColor(card, radio); }
  }
  syncMugRows(card);
  await saveMugWish(card);
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', prefillMugsForEdit, { once: true });
} else {
  prefillMugsForEdit();
}
document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  // The pack picker can fire on its own (the product-options app re-selects the pack, e.g. on the edit page or
  // when adding). Only a real pack switch re-renders the mug card unticked, so once the picker settles, re-read
  // the card: ticks still there keep the pick, an unticked fresh card drops it. Picks from a previous page
  // (edit return trip via /cart) are left alone for the cart sync to add.
  if (target.closest('s-variant-picker')) {
    if (!mugWishSavedHere) return;
    const started = Date.now();
    const settle = () => {
      if (document.querySelector('s-variant-picker[aria-busy="true"]') && Date.now() - started < 6000) {
        setTimeout(settle, 150);
        return;
      }
      const freshCard = document.querySelector('[data-bfcm-mug]');
      if (freshCard) { syncMugRows(freshCard); saveMugWish(freshCard); } else { clearMugWish(); }
    };
    setTimeout(settle, 150);
    return;
  }
  if (!target.matches('[data-bfcm-mug-toggle], [data-bfcm-mug-color]')) return;
  const card = target.closest('[data-bfcm-mug]');
  if (!card) return;
  syncMugRows(card);
  if (target.matches('[data-bfcm-mug-color]')) showMugColor(card, target)
  saveMugWish(card);
});

let syncingFree = null;
let lastSyncedCart = null;
function syncBfcmFree() {
  syncingFree ||= doSyncBfcmFree().finally(() => { syncingFree = null; });
  return syncingFree;
}

/**
 * Matching mugs card: the cart itself decides which shirt each mug is for. After every refresh, point each mug's
 * dropdown at its saved shirt (dropping "Pick a shirt" once it has one) and only offer chips for shirts without a mug.
 */
function reconcileMugRows(cart) {
  const byKey = new Map(cart.items.map((item) => [item.key, item]));
  document.querySelectorAll('[data-mug-umbrella]').forEach((card) => {
    const used = new Set();
    card.querySelectorAll('select[data-mug-shirt-select]').forEach((select) => {
      const line = byKey.get(select.dataset.lineKey);
      const shirt = String(line?.properties?._matching_tee_design || '').trim();
      if (!shirt || !select.querySelector(`option[value="${CSS.escape(shirt)}"]`)) return;
      used.add(shirt);
      select.querySelector('option[value=""]')?.remove();
      select.value = shirt;
      if (line?.properties) select.dataset.lineProperties = JSON.stringify(line.properties);
    });
    // Shirts shown on any mug row count as taken, including ones the cart is about to save.
    card.querySelectorAll('select[data-mug-shirt-select]').forEach((select) => { if (select.value) used.add(select.value); });
    card.querySelectorAll('select[data-mug-shirt-select]').forEach((select) => {
      for (const option of select.options) {
        if (option.value) option.disabled = option.value !== select.value && used.has(option.value);
      }
    });
    card.querySelectorAll('[data-mug-chip][data-shirt]').forEach((chip) => { chip.hidden = used.has(chip.dataset.shirt); });
  });
  applyPendingMugs();
}

/** Returns true when the cart was changed. */
async function doSyncBfcmFree() {
  const config = bfcmFreeConfig();
  if (!config?.bundleProductId || !config.freeVariantIds?.length) return false;
  const root = window.Shopify?.routes?.root || '/';
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
  try {
    const cart = await (await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })).json();
    lastSyncedCart = cart;
    reconcileMugRows(cart);
    const bundleIds = config.bundleProductIds?.length ? config.bundleProductIds : [config.bundleProductId];
    const bundleLines = cart.items.filter((item) => bundleIds.includes(item.product_id));
    const hasBundle = bundleLines.length > 0;
    // Shirts in the cart: the pack size is the first character of the variant title ("3x Bundle", "5+ Bundle").
    const shirts = bundleLines.reduce((sum, item) => sum + (parseInt(String(item.variant_title || '').charAt(0), 10) || 1) * item.quantity, 0);
    const updates = {};
    const adds = [];
    // Kids bundle: its shirts get the tablet wallpaper instead of the phone one.
    const swap = config.wallpaperSwap?.phone && config.wallpaperSwap?.tablet ? config.wallpaperSwap : null;
    const kidsIds = config.kidsBundleIds || [];
    const kidsShirts = bundleLines
      .filter((item) => kidsIds.includes(item.product_id))
      .reduce((sum, item) => sum + (parseInt(String(item.variant_title || '').charAt(0), 10) || 1) * item.quantity, 0);
    const wantedList = config.freeVariantIds.map((variantId) => {
      // One of each free extra per shirt in the bundle(s); once-per-order extras (Skip the Line) just one.
      let wanted = (config.oncePerOrderIds || []).includes(variantId) ? 1 : Math.max(shirts, 1);
      if (swap && variantId === swap.phone) wanted = shirts - kidsShirts;
      return [variantId, hasBundle ? wanted : 0];
    });
    if (swap) wantedList.push([swap.tablet, hasBundle ? kidsShirts : 0]);
    for (const [variantId, perCart] of wantedList) {
      const lines = cart.items.filter((item) => item.variant_id === variantId);
      if (perCart <= 0) {
        for (const line of lines) updates[line.key] = 0;
      } else if (!lines.length) {
        adds.push({ id: variantId, quantity: perCart });
      } else {
        lines.forEach((line, index) => {
          const wanted = index === 0 ? perCart : 0;
          if (line.quantity !== wanted) updates[line.key] = wanted;
        });
      }
    }
    // Matching mug ticked on the Black Friday product page: add it once a new bundle line has arrived.
    // The deal mug is bundle-only: drop every mug when no bundle is in the cart.
    const mugLines = config.mugProductId ? cart.items.filter((item) => item.product_id === config.mugProductId) : [];
    if (!hasBundle) {
      for (const item of mugLines) updates[item.key] = 0;
    }
    // Every mug needs a shirt: give any mug without one the first shirt that doesn't have a mug yet.
    const unassigned = hasBundle ? mugLines.filter((item) => !item.properties?._matching_tee_design) : [];
    if (unassigned.length) {
      const taken = new Set(mugLines.map((item) => String(item.properties?._matching_tee_design || '')).filter(Boolean));
      for (const item of unassigned) {
        let shirt = 1;
        while (taken.has(String(shirt)) && shirt < Math.max(shirts, 1)) shirt += 1;
        taken.add(String(shirt));
        try {
          await rewriteMugLine({
            key: item.key,
            variantId: item.variant_id,
            quantity: item.quantity,
            properties: { ...(item.properties || {}), 'Matching tee design': (config.mugShirtTemplate || 'Shirt [n]').replace('[n]', String(shirt)), _matching_tee_design: String(shirt), _mug_order: item.properties?._mug_order || mugOrderStamp() },
          });
        } catch {}
      }
      return true;
    }
    const mugWish = readMugWish();
    // The line being edited (mugWish.replaces) is not the new one, and its shirts don't count as earlier ones.
    const newBundleLine = mugWish && hasBundle
      ? bundleLines.find((item) => !mugWish.keys.includes(item.key) && item.key !== mugWish.replaces)
      : null;
    if (newBundleLine) {
      clearMugWish();
      // Edit: the edited line's old mugs make way for the mugs picked on the edit page.
      const replacing = new Set(mugWish.replaceMugKeys || []);
      for (const item of mugLines) {
        if (replacing.has(item.key)) updates[item.key] = 0;
      }
      // One mug per shirt, numbered onto the shirts that don't have a mug yet (shoppers can re-pick on the line).
      const used = new Set(cart.items
        .filter((item) => item.product_id === mugWish.productId && item.properties?._matching_tee_design && !replacing.has(item.key))
        .map((item) => String(item.properties._matching_tee_design)));
      const totalShirts = Math.max(shirts, 1);
      // Ticked shirts on the bundle page, shifted past shirts from bundles already in the cart.
      const earlierShirts = bundleLines
        .filter((item) => mugWish.keys.includes(item.key) && item.key !== mugWish.replaces)
        .reduce((sum, item) => sum + (parseInt(String(item.variant_title || '').charAt(0), 10) || 1) * item.quantity, 0);
      let free = Array.isArray(mugWish.shirts) && mugWish.shirts.length
        ? mugWish.shirts.map((shirt) => shirt + earlierShirts).filter((shirt) => shirt <= totalShirts)
        : [];
      if (!free.length && !mugWish.replaces) {
        for (let shirt = 1; shirt <= totalShirts && free.length < mugWish.count; shirt += 1) {
          if (!used.has(String(shirt))) free.push(shirt);
        }
      }
      for (const shirt of free) {
        adds.push({
          // Colour picked for that shirt on the bundle page (keys are the page's own shirt numbers).
          id: Number(mugWish.variants?.[shirt - earlierShirts]) || mugWish.variantId,
          quantity: 1,
          properties: {
            'Matching tee design': (mugWish.shirtTemplate || 'Shirt [n]').replace('[n]', String(shirt)),
            _matching_tee_design: String(shirt),
            _mug_order: mugOrderStamp(),
            _cart_offer: 'bfcm-mug',
            _cart_offer_placement: 'product',
            _cart_offer_price: mugWish.price || '',
          },
        });
      }
    }
    let changed = false;
    if (Object.keys(updates).length) {
      const response = await fetch(`${root}cart/update.js`, { method: 'POST', headers: json, body: JSON.stringify({ updates }) });
      changed ||= response.ok;
    }
    if (adds.length) {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: json, body: JSON.stringify({ items: adds }) });
      changed ||= response.ok;
    }
    return changed;
  } catch {
    return false;
  }
}

// Every cart change in the theme ends in CartItems.refreshMountedCarts(): check the free extras after each one.
let refreshCarts = null;
customElements.whenDefined('s-cart').then((CartItems) => {
  if (typeof CartItems?.refreshMountedCarts !== 'function' || CartItems.cpBfcmWrapped) return;
  const original = CartItems.refreshMountedCarts.bind(CartItems);
  refreshCarts = original;
  CartItems.cpBfcmWrapped = true;
  CartItems.refreshMountedCarts = async (...args) => {
    // The cart change is already saved when a refresh starts, so check the extras alongside the redraw rather
    // than after it (halves the wait on mug colour/shirt swaps). A sync already in flight read an older cart.
    const pendingSync = syncingFree;
    const freshSync = (async () => {
      if (pendingSync) { try { await pendingSync; } catch {} }
      return syncBfcmFree();
    })();
    const [result, changed] = await Promise.all([original(...args), freshSync.catch(() => false)]);
    if (lastSyncedCart) reconcileMugRows(lastSyncedCart);
    if (changed) {
      try { await original(); } catch {}
    }
    return result;
  };
});

async function tagAndRefresh() {
  const freeChanged = await syncBfcmFree();
  const tagged = await tagBfcmDigital();
  if (freeChanged || tagged) {
    try { await (refreshCarts || customElements.get('s-cart')?.refreshMountedCarts?.bind(customElements.get('s-cart')))?.(); } catch {}
  }
}
function watchDrawerForBfcm() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  new MutationObserver(() => {
    if (!drawer.hasAttribute('open')) return;
    tagAndRefresh();
    // The drawer can open before the newly added line renders.
    setTimeout(tagAndRefresh, 1200);
  })
    .observe(drawer, { attributes: true, attributeFilter: ['open'] });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchDrawerForBfcm, { once: true });
} else {
  watchDrawerForBfcm();
}
window.addEventListener('load', tagAndRefresh);

// Reference photos in the cart open in a pop-up (snippets/cart-image-viewer.liquid) instead of a new tab.
// Each photo row (data-ref-gallery, one per shirt) is its own swipe set, so a pack's shirts never mix.
// Without the viewer (or with a modifier key held) the link keeps its normal new-tab behaviour.
const imageViewer = document.getElementById('cart-image-viewer');

if (imageViewer instanceof HTMLDialogElement) {
  const track = imageViewer.querySelector('[data-image-viewer-track]');
  const thumbs = imageViewer.querySelector('[data-image-viewer-thumbs]');
  const counter = imageViewer.querySelector('[data-image-viewer-counter]');
  const title = imageViewer.querySelector('[data-image-viewer-title]');
  const prev = imageViewer.querySelector('[data-image-viewer-prev]');
  const next = imageViewer.querySelector('[data-image-viewer-next]');
  const slideTemplate = imageViewer.querySelector('template[data-image-viewer-slide]');
  const thumbTemplate = imageViewer.querySelector('template[data-image-viewer-thumb]');
  const defaultTitle = title?.textContent || '';
  let urls = [];
  let current = 0;

  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replace(`[${key}]`, String(value)), template || '');

  const show = (index) => {
    current = Math.max(0, Math.min(urls.length - 1, index));
    if (counter) counter.textContent = urls.length > 1 ? fill(imageViewer.dataset.counterTemplate, { current: current + 1, total: urls.length }) : '';
    if (prev instanceof HTMLButtonElement) prev.disabled = current === 0;
    if (next instanceof HTMLButtonElement) next.disabled = current === urls.length - 1;
    thumbs?.querySelectorAll('button').forEach((thumb, i) => {
      thumb.setAttribute('aria-current', String(i === current));
      if (i === current) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  };

  const goTo = (index, smooth = true) => {
    const slide = track?.children[Math.max(0, Math.min(urls.length - 1, index))];
    if (!(track instanceof HTMLElement) || !(slide instanceof HTMLElement)) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: smooth ? 'smooth' : 'instant' });
    show(index);
  };

  const colorsBar = imageViewer.querySelector('[data-image-viewer-colors]');
  const colorName = imageViewer.querySelector('[data-image-viewer-color-name]');
  const swatches = imageViewer.querySelector('[data-image-viewer-swatches]');
  const swatchTemplate = imageViewer.querySelector('template[data-image-viewer-swatch]');

  // Swaps the photos (slides + thumbnails) to a new list.
  const showSet = (list, start = 0) => {
    urls = list.filter(Boolean);
    if (!urls.length) return false;
    const single = urls.length < 2;
    [prev, next, thumbs].forEach((el) => el?.toggleAttribute('data-single', single));

    track?.replaceChildren(...urls.map((url) => {
      const slide = slideTemplate.content.firstElementChild.cloneNode(true);
      slide.querySelector('img').src = url;
      return slide;
    }));
    thumbs?.replaceChildren(...urls.map((url, i) => {
      const thumb = thumbTemplate.content.firstElementChild.cloneNode(true);
      thumb.querySelector('img').src = url;
      thumb.setAttribute('aria-label', fill(imageViewer.dataset.goToTemplate, { number: i + 1 }));
      thumb.addEventListener('click', () => goTo(i));
      return thumb;
    }));
    return true;
  };

  // Optional colour swatches: options.colors = [{ id, name, swatch, available, urls }], options.selected = colour id,
  // options.onPick(id) runs when the shopper picks a colour (the mug card mirrors it into their mug choice).
  const showColors = (options) => {
    const colors = (options?.colors || []).filter((c) => c.urls?.length);
    if (!colorsBar || !swatches || !swatchTemplate || colors.length < 2) {
      if (colorsBar) colorsBar.hidden = true;
      return null;
    }
    let selected = colors.find((c) => String(c.id) === String(options.selected)) || colors[0];
    const mark = () => {
      swatches.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.colorId === String(selected.id))));
      if (colorName) colorName.textContent = selected.name;
    };
    swatches.replaceChildren(...colors.map((color) => {
      const button = swatchTemplate.content.firstElementChild.cloneNode(true);
      button.dataset.colorId = String(color.id);
      button.style.backgroundColor = color.swatch;
      button.setAttribute('aria-label', color.name);
      button.title = color.name;
      if (color.available === false) button.disabled = true;
      button.addEventListener('click', () => {
        if (selected === color) return;
        selected = color;
        mark();
        // Stay on the same angle (front, side, back) in the new colour.
        const angle = current;
        showSet(color.urls);
        goTo(Math.min(angle, color.urls.length - 1), false);
        options.onPick?.(color.id);
      });
      return button;
    }));
    colorsBar.hidden = false;
    mark();
    return selected;
  };

  // Opens the viewer on a list of photo URLs. Product pages (s-ref-uploads.js) use it via window.cpOpenImageViewer.
  const openUrls = (list, start = 0, heading = '', was = '', options = null) => {
    const selectedColor = showColors(options);
    const firstList = selectedColor ? selectedColor.urls : list;
    if (!showSet(firstList)) return;
    if (title) {
      title.textContent = heading || defaultTitle;
      // Optional crossed-out "was" price beside the heading (the mug's bargain).
      if (was) {
        const struck = document.createElement('s');
        struck.style.cssText = 'margin-inline-start:0.5rem;font-weight:400;opacity:0.6';
        struck.textContent = was;
        title.append(struck);
      }
    }

    if (!imageViewer.open) imageViewer.showModal();
    requestAnimationFrame(() => goTo(selectedColor ? 0 : start, false));
  };
  window.cpOpenImageViewer = openUrls;

  const open = (link) => {
    const gallery = link.closest('[data-ref-gallery]');
    const links = [...(gallery || link.parentElement || document).querySelectorAll('a[data-ref-image]')];
    openUrls(links.map((a) => a.href), Math.max(0, links.indexOf(link)), gallery?.dataset.refGalleryTitle);
  };

  window.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[data-ref-image]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    open(link);
  });

  // Swiping: the counter, arrows and thumbnails follow whichever photo has snapped into view.
  let scrollTimer = 0;
  track?.addEventListener('scroll', () => {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      if (!track.clientWidth) return;
      const index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
      if (index !== current) show(index);
    }, 80);
  }, { passive: true });

  const isRtl = () => getComputedStyle(imageViewer).direction === 'rtl';
  prev?.addEventListener('click', () => goTo(current - 1));
  next?.addEventListener('click', () => goTo(current + 1));
  imageViewer.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const forward = (event.key === 'ArrowRight') !== isRtl();
    goTo(current + (forward ? 1 : -1));
  });

  // Tapping the dark area around the card closes it; slides are cleared so the next shirt never flashes the last one.
  imageViewer.addEventListener('click', (event) => {
    if (event.target === imageViewer) imageViewer.close();
  });
  imageViewer.querySelector('[data-image-viewer-close]')?.addEventListener('click', () => imageViewer.close());
  imageViewer.addEventListener('close', () => {
    track?.replaceChildren();
    thumbs?.replaceChildren();
    urls = [];
  });
}

// Back from the app's /cart hand-off after an edit: finish the edit (remove the old shirt, swap in edit-page mugs)
// straight away, and let the drawer opener in layout/theme.liquid wait for it, so the cart opens already up to
// date instead of the mug popping in a few seconds later.
let cpHasPendingEdit = false;
try { cpHasPendingEdit = !!sessionStorage.getItem('cp:editing'); } catch {}
if (window.cpReturningToDrawer && cpHasPendingEdit) {
  const finishing = (async () => {
    if (!(await removeEditedOriginal())) return;
    await customElements.whenDefined('s-cart');
    await customElements.get('s-cart')?.refreshMountedCarts?.({ animate: false });
  })().catch(() => {});
  window.cpEditFinishing = Promise.race([finishing, new Promise((resolve) => setTimeout(resolve, 4000))]);
}
